/**
 * src/sync/pgAdapter.js — normalize .NET/Postgres API read responses into the
 * Firestore-document shape the frontend views already consume.
 *
 * Etap 7c/7d foundation. The Etap 7a GET `/api/v1/*` endpoints return EF
 * entities (camelCase JSON), which differ structurally from the legacy
 * Firestore reads in three ways. This module normalizes all three so the read
 * composables (PR-C / PR-G) can swap data sources without touching any view:
 *
 *  1. LIST WRAPPER. API list endpoints return `{ count, items }`; Firestore
 *     reads yielded a bare array. `unwrapList()` returns `items`.
 *
 *  2. LONG-TAIL JSONB. The API serializes the long-tail fields into a single
 *     `data` column as a JSON *string*
 *     (e.g. `offer.data = '{"emailinwestor":"…","montaz":"…"}'`). Firestore had
 *     them as top-level fields. `adaptDoc()` parses `data` and spreads it back
 *     to the top level (typed columns win on the unlikely collision — the
 *     backend mapper guarantees no overlap, see FirestorePayloadMapper.cs).
 *
 *  3. FIRESTORE TIMESTAMP. The API returns timestamps as ISO-8601 strings;
 *     Firestore returned Timestamp objects with `.toDate()` / `.seconds`. Views
 *     call `createdAt.toDate()` (OfferList:20, MyList:18, DoneList:18,
 *     ProductionList:21, CreatePdf:235) and `.seconds` (OffersReport:389 sort),
 *     so configured timestamp fields are re-wrapped in a minimal
 *     Timestamp-like shim.
 *
 * Plus a per-collection FIELD RENAME for `offers`: Firestore stored a handful
 * of columns all-lowercase (kwotanetto, kwotabrutto, ktomontuje, datazaliczki,
 * czasrealizacji, terminrealizacjikalendarz), but the EF entity + camelCase
 * JSON policy emit them camelCased (kwotaNetto, KtoMontuje → ktoMontuje, …).
 * We rename them back so `offer.kwotanetto` keeps resolving.
 *
 * Plus an offers DATE-ONLY VALUE BRIDGE (Etap 7d/PR-F). The API serializes the
 * `datazaliczki` / `terminrealizacjikalendarz` DateOnly columns as ISO
 * "yyyy-MM-dd", but the offer views' custom `toDate()` parser does
 * `dateStr.split(".")` expecting a "d.MM.yyyy"/"dd.MM.yyyy" string (ProductionPlan
 * calendar, SellerData/FinancialDataOffer date-pickers). Without conversion those
 * parsers yield `new Date(undefined, NaN, NaN)` → Invalid Date → installation
 * events vanish from the calendar. `isoToFirestoreDate()` converts the value
 * after the rename, reproducing each field's EXACT Firestore day-padding for
 * byte-parity (see OFFER_DATE_FIELD_SPEC).
 *
 * Pure module: no Firebase, no network, no Vue — trivially unit-testable.
 */

/**
 * Offer date-only fields whose VALUE format differs between Firestore
 * ("d.MM.yyyy" / "dd.MM.yyyy" string) and the API (ISO "yyyy-MM-dd"). The adapter
 * renames the keys (camelCase→lowercase) AND bridges the value so the views'
 * split(".")-based toDate() parser keeps producing valid Dates.
 *
 * `padDay` reproduces each field's EXACT Firestore write format for byte-parity
 * with the reads this replaces (the toDate() parser is padding-agnostic, but a
 * future string-equality consumer would diverge on a single-digit day):
 *   - datazaliczki              → 'd.MM.yyyy'  (NON-padded day; SellerData.vue:254
 *                                  + DataHandler.vue:143 write `format(…, 'd.MM.yyyy')`)
 *   - terminrealizacjikalendarz → 'dd.MM.yyyy' (padded day; written `format(…, 'dd.MM.yyyy')`)
 *
 * NOT included (deliberately): `terminumowy` / `terminrealizacji` are long-tail
 * JSONB fields stored verbatim as "dd.MM.yyyy" — they pass through parseData()
 * unchanged and need no bridge.
 */
const OFFER_DATE_FIELD_SPEC = Object.freeze({
  datazaliczki:              { padDay: false },
  terminrealizacjikalendarz: { padDay: true },
});

/** Canonical list of bridged offer date fields. Exported so downstream work
 *  references it instead of re-deriving the set. */
export const OFFER_DATE_FIELDS = Object.freeze(Object.keys(OFFER_DATE_FIELD_SPEC));

// Per-collection transform config. A collection absent from this map is treated
// as EMPTY_CONFIG (structural transforms only, no renames, no timestamp shim).
const COLLECTION_CONFIG = {
  teams:          { timestampFields: [] },
  vats:           { timestampFields: [] },
  wood:           { timestampFields: [] },
  users:          { timestampFields: ['createdAt'] },
  technical_data: { timestampFields: ['createdAt', 'updatedAt'] },
  quotes:         { timestampFields: ['createdAt'] },
  offers: {
    timestampFields: ['createdAt'],
    // Date-only columns: rename the key AND bridge the ISO value, per-field
    // day-padding (see OFFER_DATE_FIELD_SPEC) for Firestore byte-parity.
    dateFields: OFFER_DATE_FIELD_SPEC,
    // camelCase (EF/JSON wire) → lowercase (Firestore / view consumers).
    rename: {
      kwotaNetto: 'kwotanetto',
      kwotaBrutto: 'kwotabrutto',
      ktoMontuje: 'ktomontuje',
      dataZaliczki: 'datazaliczki',
      czasRealizacji: 'czasrealizacji',
      terminRealizacjiKalendarz: 'terminrealizacjikalendarz',
    },
  },
};

const EMPTY_CONFIG = { timestampFields: [] };

/**
 * Unwrap a list response. The API returns `{ count, items }`; tolerate a bare
 * array (already-unwrapped, or a future endpoint shape) and anything else
 * (null/error) by returning an empty array — fail-soft so a malformed response
 * renders an empty list rather than throwing in a `v-for`.
 */
export function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.items)) return response.items;
  return [];
}

/**
 * Wrap an ISO-8601 string in a minimal Firestore-Timestamp-like object so view
 * code calling `.toDate()` / `.seconds` keeps working. Idempotent: an existing
 * Timestamp (handed back by the Firestore fallback path) passes through
 * untouched; an unparseable value is returned as-is rather than producing an
 * Invalid Date shim.
 */
export function toFirestoreTimestamp(value) {
  if (value == null) return value;
  // Already a Firestore Timestamp (fallback-to-Firestore path may supply one).
  if (typeof value === 'object' && typeof value.toDate === 'function') return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value; // unparseable → leave untouched
  return {
    // Fresh Date per call, mirroring the real Firestore Timestamp.toDate()
    // contract — a consumer that mutates the result (e.g. setHours for
    // day-bucketing) must not corrupt the shim for subsequent reads.
    toDate: () => new Date(d.getTime()),
    seconds: Math.floor(d.getTime() / 1000),
    nanoseconds: 0,
  };
}

/**
 * Convert an API ISO date-only value ("yyyy-MM-dd", optionally with a trailing
 * time part) into the "d.MM.yyyy"/"dd.MM.yyyy" string the offer views' custom
 * toDate() parser (`dateStr.split(".")`) expects. Mirrors toFirestoreTimestamp's
 * contract:
 *   - null / undefined / non-string  → passthrough (DateOnly is nullable)
 *   - already "d.MM.yyyy"/"dd.MM.yyyy" → passthrough (idempotent; covers the
 *     fallback-to-Firestore path, which never sees the adapter but is harmless)
 *   - unparseable                    → passthrough untouched (never emit "NaN…")
 *
 * `padDay` (default true) controls the day component: true → "dd" (zero-padded),
 * false → "d" (leading zero stripped) so the output is byte-identical to the
 * Firestore value each field was written with. Month always stays padded ("MM"),
 * matching every Firestore write path (`format(…, 'MM')`).
 *
 * Pure string transform via regex — NO `new Date()`. DateOnly carries no
 * timezone; `new Date("2026-06-15")` parses as UTC midnight and `getDate()` in
 * PL (UTC+1/+2) can roll back a day. String→string sidesteps that footgun.
 */
export function isoToFirestoreDate(value, padDay = true) {
  if (value == null || typeof value !== 'string') return value;
  const trimmed = value.trim();
  // Already d.MM.yyyy / dd.MM.yyyy (idempotent / fallback-to-Firestore path).
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(trimmed)) return trimmed;
  // ISO yyyy-MM-dd (tolerate a trailing time component: "2026-06-15T00:00:00").
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (!m) return value; // unparseable → leave untouched
  const [, yyyy, mm, dd] = m;
  const day = padDay ? dd : String(Number(dd)); // strip leading zero when not padding
  return `${day}.${mm}.${yyyy}`;
}

/**
 * Parse the `data` JSONB column. Returns a plain object always (never throws):
 * a malformed or non-object payload degrades to `{}` so the document still
 * renders its typed columns.
 */
function parseData(data) {
  if (data == null) return {};
  // Defensive: some transports may have already parsed it to an object. Deep-copy
  // via a JSON round-trip (JSONB is plain JSON) so nested objects/arrays are NOT
  // shared with the source entity — keeps adaptDoc pure even if a view later
  // mutates a nested long-tail value. The common path (string below) is already
  // fresh because JSON.parse builds a new tree.
  if (typeof data === 'object') {
    if (Array.isArray(data)) return {};
    try {
      return JSON.parse(JSON.stringify(data));
    } catch {
      return {};
    }
  }
  if (typeof data !== 'string') return {};
  const trimmed = data.trim();
  if (trimmed === '') return {};
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Adapt a single API entity into a Firestore-shaped document for the given
 * collection. Non-object input is returned unchanged (null-safe for by-id 404
 * handling at call sites).
 */
export function adaptDoc(collection, entity) {
  if (entity == null || typeof entity !== 'object' || Array.isArray(entity)) return entity;
  const cfg = COLLECTION_CONFIG[collection] || EMPTY_CONFIG;

  // 1. Split the JSONB blob off and merge the long-tail beneath the typed
  //    columns. Destructuring `data` out also drops the raw string from the
  //    result, which the frontend never reads directly.
  const { data, ...columns } = entity;
  const longTail = parseData(data);
  const doc = { ...longTail, ...columns }; // typed columns win on collision

  // 2. Per-collection key renames (offers' all-lowercase columns).
  if (cfg.rename) {
    for (const [from, to] of Object.entries(cfg.rename)) {
      if (from in doc) {
        doc[to] = doc[from];
        if (from !== to) delete doc[from];
      }
    }
  }

  // 3. Timestamp shim for fields views call `.toDate()` / `.seconds` on.
  for (const field of cfg.timestampFields) {
    if (field in doc) doc[field] = toFirestoreTimestamp(doc[field]);
  }

  // 4. Date-only value bridge (offers): ISO "yyyy-MM-dd" → "d.MM.yyyy"/"dd.MM.yyyy",
  //    per-field day-padding for Firestore byte-parity. Runs AFTER the rename so
  //    keys are already lowercase.
  for (const [field, spec] of Object.entries(cfg.dateFields || {})) {
    if (field in doc) doc[field] = isoToFirestoreDate(doc[field], spec.padDay);
  }

  return doc;
}

/**
 * Adapt a full list response (`{ count, items }` or bare array) into an array
 * of Firestore-shaped documents.
 */
export function adaptList(collection, response) {
  return unwrapList(response).map((entity) => adaptDoc(collection, entity));
}
